import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    Typography,
    Box,
    IconButton,
    Chip,
    Menu,
    MenuItem,
} from '@mui/material';
import {
    MoreVert as MoreIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Hive as HiveIcon,
    Assignment as ChecklistIcon,
    CalendarToday as CalendarIcon,
    Cloud as WeatherIcon,
} from '@mui/icons-material';
import { Inspection } from '@types';
import { format } from 'date-fns';

interface InspectionCardProps {
    inspection: Inspection;
    onEdit?: (inspection: Inspection) => void;
    onDelete?: (inspectionId: number) => void;
    onView?: (inspection: Inspection) => void;
}

const InspectionCard: React.FC<InspectionCardProps> = ({
    inspection,
    onEdit,
    onDelete,
    onView,
}) => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleView = () => {
        handleMenuClose();
        if (onView) {
            onView(inspection);
        } else {
            navigate(`/inspections/${inspection.id}`);
        }
    };

    const handleEdit = () => {
        handleMenuClose();
        if (onEdit) {
            onEdit(inspection);
        } else {
            navigate(`/inspections/${inspection.id}/edit`);
        }
    };

    const handleDelete = () => {
        handleMenuClose();
        if (onDelete) {
            onDelete(inspection.id);
        }
    };

    const handleCardClick = () => {
        handleView();
    };

    const getImpressionColor = (impression: number) => {
        if (impression >= 3) return 'success';
        if (impression === 2) return 'warning';
        return 'error';
    };

    const getImpressionLabel = (impression: number) => {
        if (impression >= 3) return 'Good';
        if (impression === 2) return 'Fair';
        return 'Poor';
    };

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-4px)',
                },
            }}
            onClick={handleCardClick}
        >
            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                {/* Header with Date and Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            {format(new Date(inspection.date), 'MMM dd, yyyy')}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={handleMenuOpen}
                        sx={{ mt: -1, mr: -1 }}
                    >
                        <MoreIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Hive Info */}
                {inspection.hive && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <HiveIcon fontSize="small" sx={{ color: '#F8B133' }} />
                        <Typography variant="body2" color="text.secondary">
                            <strong>{inspection.hive.name}</strong>
                            {inspection.hive.location && ` - ${inspection.hive.location.name}`}
                        </Typography>
                    </Box>
                )}

                {/* Checklist Info */}
                {inspection.checklist && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <ChecklistIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                            {inspection.checklist.name}
                        </Typography>
                    </Box>
                )}

                {/* Weather */}
                {inspection.weather && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <WeatherIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                            {inspection.weather}
                            {inspection.temperature && ` • ${inspection.temperature}°C`}
                        </Typography>
                    </Box>
                )}

                {/* Notes Preview */}
                {inspection.notes && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            mt: 1.5,
                            fontStyle: 'italic',
                        }}
                    >
                        "{inspection.notes}"
                    </Typography>
                )}

                {/* Tags */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
                    <Chip
                        label={getImpressionLabel(inspection.impression)}
                        size="small"
                        color={getImpressionColor(inspection.impression)}
                        sx={{ fontSize: '0.7rem' }}
                    />
                    {inspection.attention && (
                        <Chip
                            label="Needs Attention"
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                        />
                    )}
                    {inspection.reminder && (
                        <Chip
                            label="Has Reminder"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                        />
                    )}
                </Box>
            </CardContent>

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
            >
                <MenuItem onClick={handleView}>
                    <ViewIcon fontSize="small" sx={{ mr: 1 }} />
                    View Details
                </MenuItem>
                <MenuItem onClick={handleEdit}>
                    <EditIcon fontSize="small" sx={{ mr: 1 }} />
                    Edit
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                    Delete
                </MenuItem>
            </Menu>
        </Card>
    );
};

export default InspectionCard;
